import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Bolt, Close, KeyboardArrowRight, Search } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { whatsappAPI } from '../../services/api';
import { isPlusOneNumber } from '../../data/phoneCodes';
import { ATTACH_ACCEPT, inspectAttachment } from './mediaFile';
import {
  TEMPLATE_CHAR_LIMIT,
  exampleValues,
  finishedMessage,
  isTimeoutError,
  newIdempotencyKey,
  paramLabel,
  sendBlockers,
  shouldRefreshCampaigns,
  sortTemplatesForNumber,
  splitPlaceholders,
  templateFields,
  templateSendParams,
} from './templateCampaigns';
import { wa } from './waTheme';

function wait(ms) {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

function acceptForHeader(format) {
  if (format === 'image') return 'image/jpeg,image/png,image/webp';
  if (format === 'video') return 'video/mp4,video/3gpp,video/*';
  if (format === 'document') return ATTACH_ACCEPT.document;
  if (format === 'audio') return ATTACH_ACCEPT.audio;
  return `${ATTACH_ACCEPT.document},${ATTACH_ACCEPT.media}`;
}

function headerKindOk(kind, format) {
  if (format === 'image') return kind === 'image';
  if (format === 'video') return kind === 'video';
  if (format === 'document') return kind === 'document';
  if (format === 'audio') return kind === 'audio' || kind === 'voice';
  return kind === 'image' || kind === 'video' || kind === 'document';
}

const TEMPLATE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'text', label: 'Text' },
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
  { id: 'document', label: 'Document' },
  { id: 'audio', label: 'Audio' },
];

function campaignKind(campaign) {
  const format = campaign?.header?.format;
  if (format === 'image' || format === 'video' || format === 'document' || format === 'audio') return format;
  return 'text';
}

function isUtilityCampaign(campaign) {
  return String(campaign?.category || '').trim().toLowerCase() === 'utility';
}

function previewLine(campaign) {
  return String(campaign?.body || '').replace(/\s+/g, ' ').trim();
}

function SlotText({ text, values, emphasizeEmpty }) {
  const parts = splitPlaceholders(text);
  if (!parts.length) return null;
  return parts.map((part, index) => {
    if (part.kind === 'text') return <span key={index}>{part.text}</span>;
    const value = String(values?.[part.name] ?? '');
    if (value.trim()) {
      return (
        <Box key={index} component="span" sx={{ fontWeight: 700 }}>
          {value}
        </Box>
      );
    }
    return (
      <Box
        key={index}
        component="span"
        sx={{
          color: emphasizeEmpty ? wa.fail : wa.green,
          fontWeight: 700,
          bgcolor: emphasizeEmpty ? '#fff4f4' : '#e7fce3',
          borderRadius: 0.5,
          px: 0.25,
        }}
      >
        {`{{${part.name}}}`}
      </Box>
    );
  });
}

function MessageCard({ campaign, values, emphasizeEmpty }) {
  const header = campaign.header?.format === 'text' ? campaign.header.text : '';
  const headerKind = campaign.header?.format && campaign.header.format !== 'text'
    ? campaign.header.format
    : '';
  return (
    <Box sx={{ bgcolor: wa.wallpaper, borderRadius: 1, p: 1.25 }}>
      <Box sx={{ bgcolor: '#fff', borderRadius: 1, px: 1.25, py: 1, boxShadow: '0 1px 0.5px rgba(11,20,26,.13)' }}>
        {headerKind && (
          <Typography sx={{ fontSize: 12, color: wa.meta, mb: 0.5, textTransform: 'capitalize' }}>
            {headerKind} header
          </Typography>
        )}
        {header && (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: wa.text, whiteSpace: 'pre-wrap', mb: 0.5 }}>
            <SlotText text={header} values={values} emphasizeEmpty={emphasizeEmpty} />
          </Typography>
        )}
        <Typography sx={{ fontSize: 13, color: wa.text, whiteSpace: 'pre-wrap' }}>
          <SlotText text={campaign.body} values={values} emphasizeEmpty={emphasizeEmpty} />
        </Typography>
        {campaign.buttons?.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
            {campaign.buttons.map((button) => (
              <Box key={`${button.text}-${button.url}`}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    border: `1px solid ${wa.green}`,
                    color: wa.green,
                    borderRadius: 1,
                    px: 1,
                    py: 0.25,
                    fontSize: 13,
                  }}
                >
                  <SlotText text={button.text || button.url} values={values} emphasizeEmpty={emphasizeEmpty} />
                </Box>
                {button.url && (
                  <Typography sx={{ fontSize: 12, color: wa.meta }}>
                    <SlotText text={button.url} values={values} emphasizeEmpty={emphasizeEmpty} />
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TemplateBody({ campaign, fields, values, onValue, onUseExample, fieldErrors, file, onFile, fileError, sending }) {
  const fileRef = useRef(null);
  const format = campaign.header?.format || '';
  const length = finishedMessage({ ...campaign, params: fields }, values).length;
  const over = length > TEMPLATE_CHAR_LIMIT;
  const samples = exampleValues(fields);
  const agentSamples = { ...samples };
  delete agentSamples.profile_name;
  const started = fields.some((field) => String(values[field.name] || '').trim());

  const chooseFile = (next) => {
    if (!next) return;
    const inspected = inspectAttachment({
      filename: next.name,
      mimeType: next.type,
      size: next.size,
    });
    if (!inspected.ok) {
      onFile(null, inspected.message);
      return;
    }
    if (!headerKindOk(inspected.kind, format)) {
      onFile(null, 'That file does not match this template header.');
      return;
    }
    onFile(next, '');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: wa.meta, mb: 0.25 }}>
          {fields.length ? 'Example' : 'Message'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: wa.meta, mb: 0.75 }}>
          {fields.length
            ? 'A sample of this template. The message you send uses the values you enter below.'
            : 'This template is sent as written.'}
        </Typography>
        <MessageCard campaign={campaign} values={samples} />
      </Box>
      {fields.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: wa.meta }}>Fill in the values</Typography>
            {Object.keys(agentSamples).length > 0 && (
              <Button size="small" disabled={sending} onClick={() => onUseExample(samples)}>
                Use sample values
              </Button>
            )}
          </Box>
          {fields.map((param) => (
            param.name === 'profile_name' ? (
              <Box key={param.name} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: wa.meta }}>WhatsApp name</Typography>
                <Typography sx={{ fontSize: 14, color: String(values.profile_name || '').trim() ? wa.text : wa.meta }}>
                  {String(values.profile_name || '').trim()
                    || 'Not set on WhatsApp yet. This send leaves that name out.'}
                </Typography>
              </Box>
            ) : (
              <TextField
                key={param.name}
                name={param.name}
                label={paramLabel(param.name)}
                placeholder={param.example || ''}
                value={values[param.name] || ''}
                onChange={(event) => onValue(param.name, event.target.value)}
                required
                fullWidth
                size="small"
                disabled={sending}
                error={Boolean(fieldErrors[param.name])}
                helperText={fieldErrors[param.name] || (param.example ? `Example: ${param.example}` : ' ')}
                inputProps={param.max_length ? { maxLength: Number(param.max_length) } : undefined}
                sx={{ mb: 0.5 }}
              />
            )
          ))}
        </Box>
      )}
      {started && (
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: wa.meta, mb: 0.75 }}>Your message</Typography>
          <MessageCard campaign={campaign} values={values} emphasizeEmpty />
        </Box>
      )}
      <Typography sx={{ fontSize: 12, color: over ? wa.fail : wa.meta }}>
        {over
          ? `This message is ${length} characters. Keep it within ${TEMPLATE_CHAR_LIMIT}.`
          : `${length} / ${TEMPLATE_CHAR_LIMIT} characters`}
      </Typography>
      {campaign.header_file !== 'none' && (
        <Box>
          <Button size="small" disabled={sending} onClick={() => fileRef.current?.click()}>
            {file ? 'Replace header file' : 'Attach header file'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            hidden
            accept={acceptForHeader(format)}
            onChange={(event) => {
              const next = event.target.files?.[0];
              event.target.value = '';
              chooseFile(next);
            }}
          />
          <Typography sx={{ fontSize: 12, color: fileError ? wa.fail : wa.meta, mt: 0.5 }}>
            {fileError
              || (file ? file.name : (
                campaign.header_file === 'optional'
                  ? 'A saved sample will be sent unless you attach a replacement.'
                  : 'Attach a header file. This template has no saved sample.'
              ))}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function WhatsAppTemplatePicker({
  variant = 'panel',
  contactId,
  leadId,
  e164 = '',
  profileName = '',
  onClose,
  onSent,
  onOptedOut,
  onActiveChange,
}) {
  const fileAttempt = useRef(null);
  const seenContact = useRef(contactId);
  const refetchCampaigns = useRef(null);
  const onActiveChangeRef = useRef(onActiveChange);
  const [selectedKey, setSelectedKey] = useState('');
  const [values, setValues] = useState({});
  const [file, setFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fileError, setFileError] = useState('');
  const [sending, setSending] = useState(false);
  const [view, setView] = useState(variant === 'dialog' ? 'list' : 'closed');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  onActiveChangeRef.current = onActiveChange;

  const campaignsQuery = useQuery(
    ['whatsappCampaigns'],
    async () => (await whatsappAPI.listCampaigns()).data,
    {
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  refetchCampaigns.current = campaignsQuery.refetch;
  const campaigns = useMemo(
    () => campaignsQuery.data?.campaigns || [],
    [campaignsQuery.data]
  );
  const utilityOnly = isPlusOneNumber(e164);
  const offeredCampaigns = useMemo(
    () => sortTemplatesForNumber(
      utilityOnly ? campaigns.filter(isUtilityCampaign) : campaigns,
      utilityOnly,
    ),
    [campaigns, utilityOnly]
  );
  const selected = offeredCampaigns.find((row) => row.campaign_key === selectedKey && !row.disabled) || null;
  const fields = useMemo(() => (selected ? templateFields(selected) : []), [selected]);
  const typeFilters = useMemo(
    () => TEMPLATE_FILTERS.filter((item) => (
      item.id !== 'audio' || offeredCampaigns.some((row) => campaignKind(row) === 'audio')
    )),
    [offeredCampaigns]
  );
  const visibleCampaigns = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return offeredCampaigns.filter((row) => {
      if (kind !== 'all' && campaignKind(row) !== kind) return false;
      if (!needle) return true;
      return `${row.label || ''} ${row.body || ''}`.toLowerCase().includes(needle);
    });
  }, [offeredCampaigns, kind, query]);

  useEffect(() => {
    setSelectedKey('');
    setValues({});
    setFile(null);
    setFieldErrors({});
    setFileError('');
    setQuery('');
    setKind('all');
    setView(variant === 'dialog' ? 'list' : 'closed');
    fileAttempt.current = null;
    if (seenContact.current === contactId) return;
    seenContact.current = contactId;
    refetchCampaigns.current?.();
  }, [contactId, variant]);

  useEffect(() => {
    if (!selectedKey) return;
    if (!offeredCampaigns.some((row) => row.campaign_key === selectedKey && !row.disabled)) {
      setSelectedKey('');
      setView((current) => (current === 'detail' ? 'list' : current));
    }
  }, [offeredCampaigns, selectedKey]);

  useEffect(() => {
    onActiveChangeRef.current?.(variant === 'panel' && view !== 'closed');
  }, [variant, view]);

  useEffect(() => () => onActiveChangeRef.current?.(false), []);

  useEffect(() => {
    if (!fields.some((field) => field.name === 'profile_name')) return;
    const stored = String(profileName || '').trim();
    setValues((current) => {
      if (stored) {
        if (current.profile_name === stored) return current;
        return { ...current, profile_name: stored };
      }
      if (!current.profile_name) return current;
      const next = { ...current };
      delete next.profile_name;
      return next;
    });
  }, [fields, profileName]);

  const refresh = () => campaignsQuery.refetch();

  const closePanel = () => {
    setView('closed');
    setSelectedKey('');
    setValues({});
    setFile(null);
    setFieldErrors({});
    setFileError('');
    setQuery('');
    setKind('all');
    fileAttempt.current = null;
  };

  const backToList = () => {
    if (sending) return;
    setView('list');
  };

  const choose = (row) => {
    if (row.disabled || sending) return;
    setSelectedKey(row.campaign_key);
    setValues({});
    setFile(null);
    setFieldErrors({});
    setFileError('');
    fileAttempt.current = null;
    setView('detail');
  };

  const send = async () => {
    if (sending || !selected) return;
    const blockers = sendBlockers({ ...selected, params: fields }, values, file);
    const nextErrors = {};
    blockers.missing.forEach((name) => { nextErrors[name] = 'Required'; });
    setFieldErrors(nextErrors);
    setFileError(blockers.fileRequired ? 'Attach a header file before sending this template.' : '');
    if (blockers.missing.length || blockers.tooLong || blockers.fileRequired) return;

    const params = templateSendParams(fields, values);
    const signature = JSON.stringify({
      campaign: selected.campaign_key,
      params,
      file: file ? `${file.name}:${file.size}:${file.lastModified}` : '',
    });
    if (!fileAttempt.current || fileAttempt.current.signature !== signature) {
      fileAttempt.current = {
        signature,
        key: newIdempotencyKey(leadId, selected.campaign_key),
        mediaId: null,
      };
    }
    const attempt = fileAttempt.current;
    const campaignKey = selected.campaign_key;
    setSending(true);
    try {
      let mediaId = attempt.mediaId;
      if (file && !mediaId) {
        const uploaded = await whatsappAPI.uploadMedia(contactId, file);
        mediaId = uploaded.data?.media?.id || null;
        if (!mediaId) {
          toast.error('Upload did not return a file id');
          return;
        }
        attempt.mediaId = mediaId;
      }

      let rateTries = 0;
      let timeoutTries = 0;
      let response;
      for (;;) {
        try {
          response = await whatsappAPI.sendTemplate(contactId, {
            campaign_key: campaignKey,
            params,
            idempotency_key: attempt.key,
            ...(leadId ? { lead_id: leadId } : {}),
            ...(mediaId ? { header_media_id: mediaId } : {}),
          });
          break;
        } catch (err) {
          const code = err.response?.data?.code;
          if (code === 'RATE_LIMITED' && rateTries < 2) {
            rateTries += 1;
            toast('Too many sends. Retrying the same template…');
            await wait(2000);
            continue;
          }
          if (isTimeoutError(err) && timeoutTries < 1) {
            timeoutTries += 1;
            toast('The send timed out. Checking the same template again…');
            await wait(1000);
            continue;
          }
          throw err;
        }
      }

      fileAttempt.current = null;
      const replayed = response.status === 200 || response.data?.replayed === true;
      toast.success(replayed
        ? 'This template was already sent.'
        : 'Template accepted. Delivery updates when WhatsApp confirms it.');
      if (variant === 'panel') closePanel();
      else {
        setValues({});
        setFile(null);
        setFieldErrors({});
      }
      onSent?.();
    } catch (err) {
      const code = err.response?.data?.code;
      const timedOut = isTimeoutError(err);
      const message = code === 'RATE_LIMITED'
        ? 'Too many sends. Press Send template again to retry this same message.'
        : (err.response?.data?.message || (timedOut
          ? 'The send timed out. It was not sent again. Press Send template to check this same message.'
          : 'Could not send the template'));
      if (code === 'MISSING_PARAM' && err.response?.data?.param) {
        setFieldErrors({ [err.response.data.param]: 'Required' });
      }
      if (code === 'HEADER_MEDIA_REQUIRED') {
        setFileError('Attach a header file before sending this template.');
      }
      if (shouldRefreshCampaigns(code)) refresh();
      if (code === 'OPTED_OUT') onOptedOut?.();
      if (err.response && code !== 'RATE_LIMITED') fileAttempt.current = null;
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const templateForm = selected ? (
    <TemplateBody
      campaign={selected}
      fields={fields}
      values={values}
      onValue={(name, value) => {
        setValues((current) => ({ ...current, [name]: value }));
        setFieldErrors((current) => ({ ...current, [name]: '' }));
      }}
      onUseExample={(samples) => {
        const next = { ...samples };
        delete next.profile_name;
        const stored = String(profileName || '').trim();
        if (stored) next.profile_name = stored;
        setValues(next);
        setFieldErrors({});
        fileAttempt.current = null;
      }}
      fieldErrors={fieldErrors}
      file={file}
      onFile={(next, message) => {
        setFile(next);
        setFileError(message || '');
        fileAttempt.current = null;
      }}
      fileError={fileError}
      sending={sending}
    />
  ) : null;

  const sendButton = (
    <Button
      variant="contained"
      disabled={sending || !selected}
      onClick={send}
      sx={{ bgcolor: wa.send, textTransform: 'none', '&:hover': { bgcolor: '#008f72' } }}
    >
      {sending ? 'Sending…' : 'Send template'}
    </Button>
  );

  const pickerBody = (
    <Box>
      <Typography sx={{ fontSize: 12, color: wa.meta, mb: 1 }}>
        A template does not reopen the 24-hour window. Only a message from the parent does that.
      </Typography>
      {utilityOnly && (
        <Typography sx={{ fontSize: 12, color: wa.meta, mb: 1 }}>
          Only utility templates can be sent to this number.
        </Typography>
      )}
      {campaignsQuery.isError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={<Button color="inherit" size="small" onClick={refresh}>Refresh</Button>}
        >
          {campaignsQuery.error?.response?.data?.message || 'Could not load templates'}
        </Alert>
      )}
      {campaignsQuery.isLoading && campaigns.length === 0 ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={22} sx={{ color: wa.green }} /></Box>
      ) : campaigns.length === 0 ? (
        campaignsQuery.isError ? null : (
          <Typography sx={{ fontSize: 13, color: wa.meta }}>No templates are available.</Typography>
        )
      ) : offeredCampaigns.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: wa.meta }}>No utility templates are available.</Typography>
      ) : view === 'detail' && templateForm ? (
        templateForm
      ) : (
        <Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Search templates"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Search templates' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: wa.meta }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <Box role="group" aria-label="Template type" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
            {typeFilters.map((item) => {
              const active = kind === item.id;
              return (
                <Box
                  key={item.id}
                  component="button"
                  type="button"
                  aria-pressed={active}
                  aria-label={item.id === 'all' ? 'All templates' : `${item.label} templates`}
                  onClick={() => setKind(item.id)}
                  sx={{
                    border: `1px solid ${active ? wa.green : wa.border}`,
                    bgcolor: active ? '#e7fce3' : '#fff',
                    color: active ? wa.green : wa.text,
                    borderRadius: 999,
                    px: 1.25,
                    py: 0.35,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    cursor: 'pointer',
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
          {visibleCampaigns.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: wa.meta, py: 2 }}>No templates match.</Typography>
          ) : (
            <Box sx={{ border: `1px solid ${wa.border}`, borderRadius: 1, overflow: 'auto', maxHeight: variant === 'dialog' ? 360 : 280 }}>
              {visibleCampaigns.map((row) => {
                const active = row.campaign_key === selectedKey;
                const count = templateFields(row).filter((field) => field.name !== 'profile_name').length;
                const kindLabel = TEMPLATE_FILTERS.find((item) => item.id === campaignKind(row))?.label || 'Text';
                return (
                  <Box
                    key={row.campaign_key}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    disabled={row.disabled || sending}
                    onClick={() => choose(row)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      borderBottom: `1px solid ${wa.border}`,
                      borderLeft: active ? `3px solid ${wa.green}` : '3px solid transparent',
                      px: 1.25,
                      py: 1,
                      cursor: row.disabled ? 'not-allowed' : 'pointer',
                      bgcolor: active ? '#f3fbf4' : '#fff',
                      opacity: row.disabled ? 0.55 : 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: 14, color: wa.text, fontWeight: 700 }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: wa.meta, flexShrink: 0 }}>{kindLabel}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: wa.meta, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {previewLine(row) || 'No message text'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: row.disabled_reason ? wa.fail : wa.meta }}>
                        {row.disabled_reason || (count === 0 ? 'No values to fill' : `${count} ${count === 1 ? 'value' : 'values'} to fill`)}
                      </Typography>
                    </Box>
                    {!row.disabled && <KeyboardArrowRight sx={{ color: wa.meta, flexShrink: 0 }} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  if (variant === 'dialog') {
    return (
      <Dialog open onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          {view === 'detail' && selected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
              <IconButton aria-label="Back to templates" onClick={backToList} disabled={sending} size="small">
                <ArrowBack fontSize="small" />
              </IconButton>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selected.label}
              </Box>
            </Box>
          ) : 'Send template'}
          <Box sx={{ flexShrink: 0 }}>
            {view !== 'detail' && (
              <Button size="small" onClick={refresh} disabled={sending || campaignsQuery.isFetching}>Refresh</Button>
            )}
            <IconButton aria-label="Close" onClick={onClose} disabled={sending} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>{pickerBody}</DialogContent>
        {view === 'detail' && selected && (
          <DialogActions sx={{ px: 3, py: 1.5 }}>{sendButton}</DialogActions>
        )}
      </Dialog>
    );
  }

  if (view === 'closed') {
    return (
      <Box sx={{ px: 2, pt: 0.25, pb: 1.5, bgcolor: '#fff', display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<Bolt sx={{ fontSize: 18 }} />}
          onClick={() => setView('list')}
          sx={{
            borderRadius: 999,
            textTransform: 'none',
            borderColor: wa.green,
            color: wa.green,
            fontWeight: 700,
            px: 2.25,
            '&:hover': { borderColor: wa.green, bgcolor: '#e7fce3' },
          }}
        >
          Send Template
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', borderTop: `1px solid ${wa.border}`, display: 'flex', flexDirection: 'column', maxHeight: 'min(560px, 62vh)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, pt: 0.75 }}>
        {view === 'detail' && selected ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
            <IconButton aria-label="Back to templates" onClick={backToList} disabled={sending} size="small">
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: wa.text }}>{selected.label}</Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: wa.text, pl: 1 }}>Templates</Typography>
        )}
        <Box sx={{ flexShrink: 0 }}>
          {view === 'list' && (
            <Button size="small" onClick={refresh} disabled={sending || campaignsQuery.isFetching}>Refresh</Button>
          )}
          <IconButton aria-label="Close templates" onClick={closePanel} disabled={sending} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ overflow: 'auto', px: 2, pb: 1.5 }}>{pickerBody}</Box>
      {view === 'detail' && selected && (
        <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${wa.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          {sendButton}
        </Box>
      )}
    </Box>
  );
}
